/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dimensions, FlatList, View } from "react-native";
import { Redirect, RouteComponentProps, useLocation } from "react-router-dom";
import qs from "qs";
import md5 from "blueimp-md5";
import { NetworkStatus, useLazyQuery, useMutation } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { SelfObject, RootState } from "store/types";

import {
  Alert,
  FadeInOutView,
  LoadingDots,
  LoadingView,
  Text,
  TutorialModal,
} from "UpduoLibrary";
import {
  skaleWhite,
  skaleWebTextLightGray,
  upduoPrimaryBlue,
  upduoLightGray,
  upduoPrimaryDark,
  skaleWebBackgroundGray,
} from "config/UpduoColors";
import {
  addPubNubSubscribedMessage,
  initializePubNub,
} from "config/UpduoPubNub";
import {
  homeResetState,
  chatResetState,
  instantiateMemberUser,
} from "config/ResetState";
import { asyncCallApi, callApi } from "config/UpduoService";
import { getActiveSummit, getNodes } from "config/Utilities";
import EmptyCalendarSVG from "assets/empty-calendar.svg";
import PastSummitSVG from "assets/past-summit-placeholder.svg";
import PhotoModal from "UpduoLibrary/PhotoModal";
import { REPORT_SESSION_PROBLEM } from "graphql/internal/Mutations";
import {
  GET_ACTIVE_SUMMIT_LIST,
  GET_PAST_SUMMIT_LIST,
} from "graphql/internal/Queries";
import {
  GetActiveSummitListQuery,
  ReportSessionProblemResponse,
  SummitType,
} from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { useTimerRef } from "UpduoLibrary/timers";
import SummitPreview from "components/SummitPreview";
import { getSummitBackgroundColor } from "components/SummitPreview/utils";
import MiniSummitPreview from "./MiniSummitPreview";

interface Referrer {
  from: {
    pathname: string;
  };
  search?: string;
  hash?: string;
  pathname?: string;
}

export default function HomeRoute({ history }: RouteComponentProps<{}>) {
  const dispatch = useDispatch();
  const location = useLocation() as any; //TODO fix react-router-dom types
  const referrer: Referrer = location.state?.referrer || {
    from: { pathname: "/" },
  };

  const authToken = useSelector((state: RootState) => state.authToken);
  const chatFailed = useSelector((state: RootState) => state.chatFailed);
  const chatPartnerHasCancelled = useSelector(
    (state: RootState) => state.chatPartnerHasCancelled
  );
  const matchObject = useSelector((state: RootState) => state.matchObject);
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const selfObjectRef = useRef<SelfObject>();
  selfObjectRef.current = selfObject;

  const pastSummitList = useSelector(
    (state: RootState) => state.pastSummitList
  );
  const pastSummitListRef = useRef<any[]>([]);
  pastSummitListRef.current = pastSummitList;

  const activeSummitList = useSelector(
    (state: RootState) => state.activeSummitList
  );

  const [hasLoadedActiveSummitListOnce, setHasLoadedActiveSummitListOnce] =
    useState(false);
  const [hasLoadedPastSummitListOnce, setHasLoadedPastSummitListOnce] =
    useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const [setSummitStatusTimer, clearSummitStatusTimer] = useTimerRef();

  const [getActiveSummitList] = useLazyQuery<GetActiveSummitListQuery>(
    GET_ACTIVE_SUMMIT_LIST,
    {
      fetchPolicy: "network-only",
      onCompleted: (result) => {
        if (result?.userActiveSummits) {
          const activeSummitList = getNodes(result.userActiveSummits);
          dispatch(
            ActionCreators.setActiveSummitList(activeSummitList as any[])
          );
          setHasLoadedActiveSummitListOnce(true);
        }
      },
      onError: (err) => {
        // TODO properly handle network errors
        console.error(err);
        setHasLoadedActiveSummitListOnce(true);
      },
    }
  );

  const [
    getPastSummitList,
    {
      data: getPastSummitListData,
      loading: getPastSummitListLoading,
      fetchMore,
      networkStatus,
    },
  ] = useLazyQuery(GET_PAST_SUMMIT_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (result) => {
      if (result?.userPastSummits) {
        const response = result?.userPastSummits.edges.map((edge) => edge.node);
        dispatch(ActionCreators.setPastSummitList(response));
      }
      setHasLoadedPastSummitListOnce(true);
    },
    onError: (err) => {
      // TODO properly handle network errors
      console.error(err);
      setHasLoadedPastSummitListOnce(true);
    },
  });

  const [reportSessionProblem, {}] = useMutation<ReportSessionProblemResponse>(
    REPORT_SESSION_PROBLEM,
    {
      onCompleted: () => {
        UpduoAnalytics.logEvent("app-report-other-chat-failed");
      },
    }
  );

  const _handleSuccessfulSubmission = () => {
    // Add delay for latest photoSubmitSuccessCounter value
    setTimeout(() => {
      // Submission is successful only if both small and large formats are uploaded
      dispatch(ActionCreators.setProfilePicSet(true));
    }, 500);
    setShowPhotoModal(false);
    instantiateMemberUser();
  };

  const _handleNavigateToSummitView = (
    summitId = "current",
    autoJoin = false
  ) => {
    if (autoJoin) {
      dispatch(ActionCreators.setAutoJoinSummit(true));
    }
    history.push(`/event/${summitId}`);
  };

  useEffect(() => {
    _handleGetHasSeenTutorial();
    dispatch(ActionCreators.setAutoJoinSummit(false));
    dispatch(ActionCreators.setSelectedSummit({} as SummitType));
    instantiateMemberUser(() => {
      // This method contains full execution path for initiating Home screen
      // Show alert, if applicable, after a delay to allow component to fully render
      if (chatFailed) {
        dispatch(ActionCreators.setChatAgendaModalVisible(false));
        // Post session problem to the server
        if (matchObject.skalesessionId) {
          reportSessionProblem({
            variables: {
              reportSessionProblemRequest: {
                skalesessionId: matchObject.skalesessionId,
                reason: "Agora grace period expired",
              },
            },
          });
        }
        _handleDisplaySimpleAlert("CHAT_FAILED");
      } else if (!chatPartnerHasCancelled) {
        // Show alert, after a delay to allow component to fully render
        dispatch(ActionCreators.setChatAgendaModalVisible(false));
        UpduoAnalytics.logEvent("app-report-other-chat-cancelled");
        _handleDisplaySimpleAlert("CANCEL");
      }
      chatResetState();
      const deviceId = `web_${md5(window.navigator.appVersion)}`;
      callApi(
        "getMatchStatus",
        undefined,
        (responseJson) => {
          if (responseJson.message !== "NOT_IN_QUEUE") {
            _handleNavigateToSummitView();
          } else {
            // Start PubNub websocket and channel
            initializePubNub(() => {
              addPubNubSubscribedMessage("me-update", () => {
                // Cue update of selfObject
                instantiateMemberUser();
              });
              addPubNubSubscribedMessage(
                "announcement",
                (remoteMessage: { heading: string; content: string }) => {
                  dispatch(ActionCreators.setAnnouncementObject(remoteMessage));
                }
              );
            });
            getActiveSummitList({
              variables: { isStaff: false },
            });
            getPastSummitList({
              variables: { isStaff: false },
            });
            setSummitStatusTimer(
              setInterval(() => {
                _handleRefreshSummitEntries();
              }, 1000)
            );
          }
        },
        undefined,
        undefined,
        `?${qs.stringify({
          device_id: deviceId,
        })}`
      );
    }).then((data) => {
      // TODO: add flag to toggle showing the photo modal on an org-by-org basis, so checkForPhoto is not hard-coded
      const checkForPhoto = false;
      if (checkForPhoto && data !== undefined) {
        setShowPhotoModal(!data.userHasPhoto);
      }
    });
    return () => {
      clearSummitStatusTimer();
    };
  }, [dispatch, authToken]);

  const _handleSummitPress = useCallback(
    (summitItem, autoJoin = false) => {
      const id = summitItem.hasOwnProperty("hashed_id")
        ? summitItem.hashed_id
        : summitItem.id;
      UpduoAnalytics.logEvent("user-summit-enter", {
        summitId: summitItem.id,
        summitName: summitItem.name,
      });
      dispatch(ActionCreators.setDisplayedSummitView("Summit"));
      _handleNavigateToSummitView(id, autoJoin);
    },
    [dispatch]
  );

  const _handleRefreshSummitEntries = useCallback(() => {
    const now = new Date();
    const summitsStillActive = activeSummitList.filter((summit) => {
      const endDateObject = new Date(Date.parse(summit.endDate));
      const summitHasEnded = now > endDateObject;
      return !summitHasEnded;
    });
    const summitHasBecomeInactive =
      summitsStillActive.length !== activeSummitList.length;
    if (summitHasBecomeInactive) {
      getActiveSummitList();
      getPastSummitList();
    }
  }, [activeSummitList, getActiveSummitList, getPastSummitList]);

  const _renderFooter = () => {
    if (!hasLoadedPastSummitListOnce) {
      return <LoadingView />;
    }
    return null;
  };

  const _handleCloseTutorialModal = () => {
    setShowTutorial(false);
    asyncCallApi("patchMePrimary", { has_seen_tutorial: true });
  };

  const _handleGetHasSeenTutorial = async () => {
    const responseJson = await asyncCallApi("getPrimary");
    if (responseJson?.hasOwnProperty("has_seen_tutorial")) {
      setShowTutorial(!responseJson?.["has_seen_tutorial"]);
    }
  };

  const _handleDisplaySimpleAlert = useCallback((scenario: string) => {
    // Show relevant informative alert dialogs when certain scenarios arise
    let title: string;
    let subtitle: string;
    switch (scenario) {
      case "SLOW":
        title = "You did not connect with your partner in time.";
        subtitle = "No worries -- please try again whenever you're ready!";
        break;
      case "CANCEL":
        title = "Session Ended";
        subtitle = "Your partner had to cancel.";
        break;
      case "DECLINE":
        title = "Your partner left the queue.";
        subtitle = "Try again to match with someone else!";
        break;
      case "CHAT_FAILED":
        title = "Your partner ran into video chat issues.";
        subtitle = "Please go online again when ready.";
        break;
      case "SERVER_ERROR":
        title = "Upduo is currently unavailable.";
        subtitle = "Please try again later!";
        break;
      default:
        title = "A problem was found with your last match.";
        subtitle =
          "No worries -- please go online again whenever you're ready!";
    }
    Alert(title, subtitle, [
      {
        text: "Okay",
        onPress: () => {
          chatResetState();
          homeResetState();
        },
      },
    ]);
  }, []);

  const noSummitsView = (
    <View
      style={{
        alignItems: "center",
        borderColor: upduoPrimaryBlue,
        borderWidth: 1,
        borderRadius: 12,
        flexGrow: 1,
        flexShrink: 1,
        overflow: "auto" as any,
      }}
    >
      <img
        src={EmptyCalendarSVG}
        style={{ height: 250 }}
        alt={"empty calendar"}
      />
      <Text
        style={{
          color: upduoLightGray,
          fontSize: 30,
          lineHeight: 42,
          textAlign: "center",
          margin: 50,
        }}
      >
        {
          "There is no live event at the moment.\nStay tuned for upcoming events!"
        }
      </Text>
    </View>
  );

  const activeSummit = getActiveSummit(activeSummitList);

  const upcomingSummits = activeSummitList.filter((x) => x !== activeSummit);

  const backgroundColor = getSummitBackgroundColor(activeSummit);

  if (authToken) {
    return (
      <div
        style={{
          backgroundColor: backgroundColor,
          paddingLeft: 60,
          paddingRight: 60,
          paddingTop: 32,
          paddingBottom: 52,
          boxSizing: "border-box",
        }}
        className={"fill-height-except-navbar"}
      >
        <FadeInOutView
          style={{
            padding: 0,
            backgroundColor: skaleWhite,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: skaleWebBackgroundGray,
            alignItems: "center",
            minWidth: 700,
            minHeight: 580,
            height: "100%",
          }}
        >
          {!hasLoadedActiveSummitListOnce ? (
            <View style={{ margin: "auto" }}>
              <LoadingDots />
            </View>
          ) : (
            <FadeInOutView
              style={{
                flex: 1,
                flexDirection: "row",
                paddingHorizontal: 35,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: Dimensions.get("window").width < 1000 ? "100%" : "70%",
                  paddingTop: 15,
                  paddingRight: Dimensions.get("window").width < 1000 ? 0 : 32,
                  paddingBottom: 45,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Text
                  isHeading={true}
                  style={{
                    fontSize: 22,
                    marginBottom: 20,
                    color: upduoPrimaryDark,
                  }}
                >
                  {"Live Event"}
                </Text>
                {activeSummit ? (
                  <View
                    style={{
                      flexGrow: 1,
                      flexShrink: 1,
                      flexBasis: 0,
                    }}
                  >
                    <SummitPreview
                      summit={activeSummit}
                      onConnect={() => _handleSummitPress(activeSummit, true)}
                    />
                  </View>
                ) : (
                  noSummitsView
                )}
              </div>
              {Dimensions.get("window").width < 1000 ? null : (
                <View
                  style={{
                    paddingTop: 20,
                    paddingBottom: 45,
                    paddingLeft: 32,
                    width: "30%",
                    minWidth: 250,
                  }}
                >
                  {upcomingSummits.length === 0 ? null : (
                    <>
                      <Text
                        isHeading={true}
                        style={{
                          fontSize: 22,
                          marginBottom: 25,
                          color: upduoPrimaryDark,
                        }}
                      >
                        {"Upcoming Events"}
                      </Text>
                      {upcomingSummits.map((summit) => (
                        <MiniSummitPreview
                          summit={summit}
                          active={true}
                          onPress={() => _handleSummitPress(summit)}
                        />
                      ))}
                    </>
                  )}
                  <Text
                    isHeading={true}
                    style={{
                      fontSize: 22,
                      marginBottom: 25,
                      color: upduoPrimaryDark,
                    }}
                  >
                    {"Past Events"}
                  </Text>
                  {pastSummitList.length > 0 ? (
                    <FlatList
                      scrollEnabled={true}
                      keyExtractor={(_, index) => String(index)}
                      data={pastSummitList}
                      renderItem={({ item }) => (
                        <MiniSummitPreview
                          summit={item}
                          active={false}
                          onPress={() => _handleSummitPress(item)}
                        />
                      )}
                      onEndReachedThreshold={0.5}
                      onEndReached={() => {
                        if (
                          !getPastSummitListLoading &&
                          networkStatus !== NetworkStatus.fetchMore &&
                          getPastSummitListData.userPastSummits.pageInfo
                            .hasNextPage
                        ) {
                          fetchMore({
                            variables: {
                              cursor:
                                getPastSummitListData.userPastSummits.pageInfo
                                  .endCursor,
                              isStaff: false,
                            },
                          });
                        }
                      }}
                      initialNumToRender={15}
                      ListFooterComponent={_renderFooter}
                    />
                  ) : (
                    <FadeInOutView
                      style={{
                        alignItems: "center",
                        paddingTop: 25,
                      }}
                    >
                      <img
                        src={PastSummitSVG}
                        style={{ height: 150, paddingBottom: 10 }}
                        alt={"no summits"}
                      />
                      <Text
                        style={{
                          color: skaleWebTextLightGray,
                          paddingBottom: 5,
                        }}
                      >
                        {"No past events so far!"}
                      </Text>
                    </FadeInOutView>
                  )}
                </View>
              )}
            </FadeInOutView>
          )}
        </FadeInOutView>
        <TutorialModal
          visible={showTutorial}
          onClose={_handleCloseTutorialModal}
        />
        <PhotoModal
          isVisible={showPhotoModal && !showTutorial}
          onDismiss={() => setShowPhotoModal(false)}
          updatePhoto={_handleSuccessfulSubmission}
          userHasPhoto={false}
        />
      </div>
    );
  }
  return (
    <Redirect
      to={{
        pathname: "/start",
        state: { referrer },
      }}
    />
  );
}
